import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAdminAuth } from '../../context/AdminAuthContext';
import '../../style/Admin.css';

const API = process.env.REACT_APP_API_URL || '';

const EMPTY_FORM = {
    title:   '',
    message: '',
    type:    'INFO',
    status:  'ACTIVE'
};

export default function AdminAnnouncementForm() {
    const [form, setForm]     = useState(EMPTY_FORM);
    const [saving, setSaving] = useState(false);
    const [error, setError]   = useState('');
    const { token, logout }   = useAdminAuth();
    const navigate            = useNavigate();
    const { id }              = useParams();
    const isEdit              = !!id;

    useEffect(() => {
        if (!isEdit) return;
        fetch(`${API}/api/admin/announcements/${id}`, {
            headers: { Authorization: `Bearer ${token}` }
        })
        .then(res => {
            if (res.status === 401) { logout(); navigate('/admin/login'); return null; }
            return res.json();
        })
        .then(data => { if (data) setForm(data); })
        .catch(() => setError('Failed to load announcement.'));
    }, [id]); // eslint-disable-line

    const handleChange = (e) => {
        const { name, value } = e.target;
        setForm(prev => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setSaving(true);
        try {
            const url    = isEdit
                ? `${API}/api/admin/announcements/${id}`
                : `${API}/api/admin/announcements`;
            const method = isEdit ? 'PUT' : 'POST';
            const res    = await fetch(url, {
                method,
                headers: {
                    'Content-Type': 'application/json',
                    Authorization:  `Bearer ${token}`
                },
                body: JSON.stringify(form)
            });
            if (res.status === 401) { logout(); navigate('/admin/login'); return; }
            if (res.ok) {
                navigate('/admin/dashboard?tab=announcements');
            } else {
                const data = await res.json();
                setError(data.error || 'Failed to save announcement.');
            }
        } catch {
            setError('Cannot connect to server.');
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="admin-layout">
            <header className="admin-header">
                <div className="admin-header-left">
                    <span className="admin-header-logo">KOVAN Admin</span>
                </div>
                <div className="admin-header-right">
                    <button className="admin-btn-outline" onClick={() => navigate('/admin/dashboard?tab=announcements')}>
                        ← Back to Dashboard
                    </button>
                </div>
            </header>

            <main className="admin-main">
                <h1 className="admin-page-title">
                    {isEdit ? 'Edit Announcement' : 'New Announcement'}
                </h1>

                {error && <div className="admin-error">{error}</div>}

                <form onSubmit={handleSubmit} className="admin-job-form">
                    <div className="admin-form-grid">
                        <div className="admin-form-group">
                            <label>Title *</label>
                            <input
                                name="title"
                                value={form.title}
                                onChange={handleChange}
                                placeholder="e.g. We're Hiring Cloud Engineers!"
                                required
                            />
                        </div>

                        <div className="admin-form-group">
                            <label>Type *</label>
                            <select name="type" value={form.type} onChange={handleChange} required>
                                <option value="INFO">Info</option>
                                <option value="PROMO">Promotion</option>
                                <option value="ALERT">Alert</option>
                            </select>
                        </div>

                        <div className="admin-form-group">
                            <label>Status *</label>
                            <select name="status" value={form.status} onChange={handleChange} required>
                                <option value="ACTIVE">Active (visible on homepage)</option>
                                <option value="DRAFT">Draft (hidden from public)</option>
                            </select>
                        </div>
                    </div>

                    <div className="admin-form-group admin-form-full">
                        <label>Message *</label>
                        <textarea
                            name="message"
                            value={form.message}
                            onChange={handleChange}
                            placeholder="Write the announcement message that will appear on the homepage..."
                            rows={5}
                            required
                        />
                    </div>

                    <div className="admin-form-actions">
                        <button type="submit" className="admin-btn-primary" disabled={saving}>
                            {saving ? 'Saving...' : (isEdit ? 'Save Changes' : 'Post Announcement')}
                        </button>
                        <button
                            type="button"
                            className="admin-btn-outline"
                            onClick={() => navigate('/admin/dashboard?tab=announcements')}
                        >
                            Cancel
                        </button>
                    </div>
                </form>
            </main>
        </div>
    );
}
